import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sprout, Recycle, Trees, Wind, AlertTriangle, Calendar } from "lucide-react";
import { Eyebrow, SectionHeading, FadeUp } from "../components/site/Primitives";
import SceneImage from "../components/site/SceneImage";

const PILLARS = [
  {
    icon: Sprout,
    title: "Sugarcane bagasse (Primary)",
    body: "Our primary material — sugarcane fibre left after juice extraction. Renewable, food-safe and engineered into bowls, trays and clamshells.",
  },
  {
    icon: Recycle,
    title: "Recyclable kraft",
    body: "Unbleached kraft food bags, sleeves and labels designed to flow into paper recycling streams. Easy to print, easy to recover.",
  },
  {
    icon: Trees,
    title: "FSC-aligned birchwood",
    body: "Our birchwood cutlery is sourced from responsibly managed forests — strong, smooth and fully home-compostable.",
  },
  {
    icon: Wind,
    title: "Reduced plastic dependency",
    body: "We help Ghana&rsquo;s food businesses phase out single-use plastics ahead of the January 2027 Styrofoam transition.",
  },
];

export default function SustainabilityPage() {
  return (
    <div className="bg-[#0A0A09]">
      <section className="relative pt-12 pb-24 lg:pb-32 kraft-texture overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-35">
          <SceneImage
            scene="compost-return"
            eager
            className="h-full w-full object-cover"
            alt=""
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A09] via-[#0A0A09]/85 to-[#1A1A18]/40" />
        </div>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <FadeUp>
            <Eyebrow>Sustainability</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="mt-8 font-grotesk text-5xl md:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.92] text-[#F7F5F0] max-w-5xl">
              From nature.
              <br />
              Back to nature
              <span className="text-[#E85D2F]">.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-[#F7F5F0]/80 leading-relaxed font-light">
              Made from renewable plant materials that return to the earth
              without leaving harm behind. Our approach is honest, practical
              and built for Africa&rsquo;s food businesses.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Ghana Styrofoam Ban Callout */}
      <section className="bg-[#F2EEE3] text-[#0A0A09] py-20 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#E85D2F]">
              <span className="h-px w-8 bg-[#E85D2F]" />
              The change is coming
            </span>
            <h2 className="mt-6 font-grotesk text-4xl md:text-6xl font-black tracking-[-0.035em] leading-[0.92]">
              Ghana&rsquo;s Styrofoam Ban:
              <br />
              <span className="text-[#E85D2F]">What businesses need to know.</span>
            </h2>
            <p className="mt-6 text-[#0A0A09]/75 leading-relaxed font-light max-w-xl">
              From January 2027, Ghana begins its national transition away from
              Styrofoam food packaging. Businesses preparing now will lead the
              market. Those who wait will face supply shocks, price pressure
              and a scramble for alternatives.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#0A0A09] text-[#F7F5F0] px-5 py-2.5 text-sm font-medium">
                <Calendar className="h-4 w-4 text-[#E85D2F]" />
                January 2027
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#0A0A09] text-[#F7F5F0] px-5 py-2.5 text-sm font-medium">
                <AlertTriangle className="h-4 w-4 text-[#E85D2F]" />
                Government action confirmed
              </span>
            </div>
            <Link
              to="/contact"
              data-testid="ban-cta"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-7 py-4 font-semibold hover:bg-[#F47A4A] transition-colors group"
            >
              Start your transition
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <FadeUp className="lg:col-span-5">
            <div className="rounded-3xl bg-[#0A0A09] text-[#F7F5F0] p-8 md:p-10">
              <p className="font-grotesk text-[10px] uppercase tracking-[0.25em] text-[#E85D2F] font-semibold">
                Yesterday → Today
              </p>
              <div className="mt-8 grid grid-cols-2 gap-6">
                <div>
                  <p className="font-grotesk text-5xl font-black tracking-tighter text-[#F7F5F0]/40 line-through decoration-[#E85D2F]">
                    FOAM
                  </p>
                  <p className="mt-3 text-xs text-[#F7F5F0]/50 uppercase tracking-wider">Yesterday</p>
                </div>
                <div>
                  <p className="font-grotesk text-5xl font-black tracking-tighter text-[#E85D2F]">
                    FIBER
                  </p>
                  <p className="mt-3 text-xs text-[#F7F5F0]/50 uppercase tracking-wider">Today</p>
                </div>
              </div>
              <p className="mt-8 text-sm text-[#F7F5F0]/70 leading-relaxed">
                Switching from petroleum-based foam to plant-based fibre is the
                single biggest packaging upgrade an African food business can
                make this decade.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <SectionHeading
            eyebrow="Materials & approach"
            title="Four building blocks of the BlacPac range."
          />
          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {PILLARS.map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.08}>
                <div className="rounded-3xl border border-[#E85D2F]/15 bg-[#121211] p-8 lg:p-10 hover:border-[#E85D2F]/40 transition-colors">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E85D2F] text-[#0A0A09]">
                    <p.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-grotesk text-2xl font-bold text-[#F7F5F0] tracking-tight">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-base text-[#F7F5F0]/70 leading-relaxed font-light">
                    {p.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Honest statement */}
      <section className="py-24 lg:py-32 bg-[#1A1A18] relative overflow-hidden border-y border-[#F7F5F0]/5">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#E85D2F]/15 blur-3xl" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <Eyebrow className="!text-[#E85D2F]">Our commitment</Eyebrow>
          </div>
          <div className="lg:col-span-9">
            <p className="font-grotesk text-3xl md:text-5xl text-[#F7F5F0] leading-[1.05] tracking-tight font-bold">
              &ldquo;We won&rsquo;t overclaim certifications. We will help your
              business move further from conventional single-use plastic — with
              packaging that performs and looks the part.&rdquo;
            </p>
            <p className="mt-8 text-[#F7F5F0]/70 max-w-2xl font-light leading-relaxed">
              Real progress on sustainability happens through honest choices,
              measured upgrades and partners who&rsquo;ll stand behind the
              materials they sell. That&rsquo;s the standard we hold ourselves
              to.
            </p>
          </div>
        </div>
      </section>

      {/* Supply chain */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>Food-service supply chain</Eyebrow>
            <h2 className="mt-6 font-grotesk text-4xl md:text-5xl font-black tracking-tighter leading-[0.95] text-[#F7F5F0]">
              A more sustainable supply, end to end.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            {[
              { t: "Sourcing", b: "Container-load purchases from vetted bagasse, kraft and birchwood manufacturers." },
              { t: "Finishing", b: "Sleeve conversion, sticker printing and brand application handled locally in Accra." },
              { t: "Distribution", b: "Stockholding in Accra with delivery partners across Ghana." },
              { t: "Continuous improvement", b: "Material reviews and supplier audits to keep our range moving in the right direction." },
            ].map((row, i) => (
              <FadeUp key={row.t} delay={i * 0.06}>
                <div className="flex gap-6 border-b border-[#F7F5F0]/10 pb-6 last:border-0">
                  <span className="font-grotesk italic text-[#E85D2F] text-4xl leading-none w-16 shrink-0 font-black tracking-tighter">
                    0{i + 1}
                  </span>
                  <div>
                    <h3 className="font-grotesk text-xl font-bold text-[#F7F5F0]">
                      {row.t}
                    </h3>
                    <p className="mt-2 text-[#F7F5F0]/65 leading-relaxed">
                      {row.b}
                    </p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="rounded-[2rem] bg-[#F2EEE3] text-[#0A0A09] p-10 md:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <h3 className="font-grotesk text-3xl md:text-5xl font-black tracking-[-0.03em] leading-[0.95] max-w-2xl">
              Ready to move your menu off Styrofoam — before January 2027?
            </h3>
            <Link
              to="/contact"
              data-testid="sustainability-bottom-cta"
              className="inline-flex items-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-7 py-4 font-semibold hover:bg-[#F47A4A] transition-colors"
            >
              Talk to BlacPac
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
